import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { ACTIVE_STORE_EVENT, getActiveStoreId } from "@/lib/activeStore";

/**
 * Iter-416 — store-scope tag: "🏬 {store}" shown in vendor card headers
 * ONLY when the account has 2+ stores, so owners always know which
 * store a card is editing. Reactive to the store switcher (listens to
 * the active-store event). Hidden entirely for single-store owners.
 */

const OPTS = {
  headers: { "X-App-Platform": "nz_classified" },
  params: { platform: "nz_classified" },
};

export function useActiveStore() {
  const [activeId, setActiveId] = useState(getActiveStoreId());
  useEffect(() => {
    const onChange = (e) => setActiveId(e.detail?.id || null);
    window.addEventListener(ACTIVE_STORE_EVENT, onChange);
    return () => window.removeEventListener(ACTIVE_STORE_EVENT, onChange);
  }, []);

  const storesQ = useQuery({
    queryKey: ["vendor-stores"],
    retry: false,
    staleTime: 60_000,
    queryFn: async () =>
      (await sharedApi.get("/classifieds/engagement/vendors/me/stores", OPTS)).data,
  });

  const stores = storesQ.data?.stores || [];
  const current = stores.find((st) => st.id === activeId) || stores[0] || null;
  return { stores, current, multi: stores.length > 1 };
}

export default function StoreScopeTag() {
  const { current, multi } = useActiveStore();
  if (!multi || !current) return null;
  return (
    <span
      data-testid="store-scope-tag"
      className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-bold px-2.5 py-0.5 whitespace-nowrap"
    >
      🏬 {current.business_name}
    </span>
  );
}
